import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import api from '../../services/api';
import { Sparkles, Wallet, CheckCircle2 } from 'lucide-react';

/**
 * CreditBalanceBanner
 * ====================
 * Shows the signed-in customer their account credit balance alongside a
 * contextual hint for the checkout amount they're about to pay.
 *
 * States rendered:
 *   - balance === 0       → renders nothing (don't clutter the UI).
 *   - balance >= amount   → "Fully covered by your $B credit — no card needed."
 *   - 0 < balance < amount→ "You have $B credit. Credit auto-applies once it
 *                            fully covers the total; otherwise you pay by
 *                            card and your credit stays saved for later."
 *
 * Props:
 *   amount     (number)   required — the total the customer is about to pay.
 *   onBalance  (function) optional — called once with `balance` after fetch,
 *                         so parents can decide to hide a 'pay by card' button.
 *   className  (string)   optional — extra wrapper classes.
 */
export default function CreditBalanceBanner({ amount, onBalance, className = '' }) {
  const [balance, setBalance] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/me/credit');
        if (cancelled) return;
        const b = Number(res.data?.balance || 0);
        setBalance(b);
        onBalance?.(b);
      } catch {
        if (!cancelled) setBalance(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // onBalance intentionally omitted — we only want one fetch per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || balance == null || balance <= 0) return null;

  const amt = Number(amount || 0);
  const fullyCovers = balance + 0.005 >= amt && amt > 0;
  const fmt = (n) => `${cs()}${Number(n || 0).toFixed(2)}`;

  if (fullyCovers) {
    return (
      <div
        className={`flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm ${className}`}
        data-testid="credit-banner-full"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-emerald-900 flex items-center gap-2">
            Fully covered by your account credit
            <Sparkles className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="text-emerald-800 mt-0.5">
            {fmt(amt)} will be deducted from your {fmt(balance)} credit balance — no card needed.
            You'll have {fmt(Math.max(0, balance - amt))} left for future purchases.
          </div>
        </div>
      </div>
    );
  }

  const missing = Math.max(0, amt - balance);
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm ${className}`}
      data-testid="credit-banner-partial"
    >
      <Wallet className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-purple-900">
          You have {fmt(balance)} in account credit
        </div>
        <div className="text-purple-800 mt-0.5">
          This payment needs {fmt(amt)}. Your credit will be applied automatically once the
          total is covered; until then your {fmt(balance)} stays saved for future payments.
          You'll pay {fmt(missing)} now.
        </div>
      </div>
    </div>
  );
}
